import { ChangeEvent, useEffect, useState } from 'react';

import {
  CategoryType,
  IDocumentsTableEntries,
  IDocumentsTableFilters,
} from '@views/pages/Patient/components/Documents/hooks/useDocumentsTable.ts';
import { t } from 'i18next';
import { SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
import {
  useDeletePatientDocumentMutation,
  usePatientDocumentDownloadMutation,
  usePatientDocumentsQuery,
} from '@common/cache';
import { fileSizeLabelFormat } from '@util/fileSizeLabelFormat.ts';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { FilePreviewContent } from '@components';
import { DocumentFile } from '@common/types';

const initialFilter: IDocumentsTableFilters = {
  sort_order: 'desc',
  sort_by: 'uploaded_on',
  page: 1,
  limit: 100,
  search: '',
  category: '',
};

export const useDocuments = (patient_id: string) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FilePreviewContent | null>(
    null,
  );
  const [downloadFile, setDownloadFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DocumentFile | null>(null);
  const [filters, setFilters] = useState<IDocumentsTableFilters>({
    ...initialFilter,
  });

  const deletePatientDocumentMutation = useDeletePatientDocumentMutation();
  const {
    data: file,
    isPending: isFileDownloading,
    mutateAsync,
    reset,
  } = usePatientDocumentDownloadMutation();

  const {
    data = {
      documents: [],
      count: 0,
    },
    isLoading,
  } = usePatientDocumentsQuery(patient_id, filters);

  useEffect(() => {
    if (!previewFile && file) {
      const urlCreator = window.URL || window.webkitURL;
      const fileUrl = urlCreator.createObjectURL(file);
      setPreviewFile({
        type: file.type,
        content: fileUrl,
      });
    }

    if (file && downloadFile) {
      const url = URL.createObjectURL(file);

      const a = document.createElement('a');
      a.href = url;
      a.download = selectedFile?.file_name || '';

      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }

      a.addEventListener('click', handleOnDownload, false);
      a.click();
      setDownloadFile(false);
    }
  }, [file, downloadFile]);

  const entries: IDocumentsTableEntries[] = data.documents.map(item => ({
    id: item.id,
    file_name: item.file.file_name,
    size: fileSizeLabelFormat(item.file.size),
    uploaded_on: dayjs(item.created_at),
    uploaded_by: {
      name: item.uploaded_by.full_name,
      role: item.uploaded_by.roles[0],
    },
    category: item.category,
    specification: item.other_specification,
    file_format: item.file.file_mime_type,
  }));

  const categoryOptions = [
    { id: '', label: 'All' },
    { id: 'care_plan', label: t('common.categoryOptions.care_plan') },
    {
      id: 'caregiver_document',
      label: t('common.categoryOptions.caregiver_document'),
    },
    { id: 'community_fax', label: t('common.categoryOptions.community_fax') },
    { id: 'general_order', label: t('common.categoryOptions.general_order') },
    {
      id: 'home_health_order',
      label: t('common.categoryOptions.home_health_order'),
    },
    { id: 'identification', label: t('common.categoryOptions.identification') },
    { id: 'insurance', label: t('common.categoryOptions.insurance') },
    { id: 'legal_document', label: t('common.categoryOptions.legal_document') },
    { id: 'med_list', label: t('common.categoryOptions.med_list') },
    { id: 'medical_record', label: t('common.categoryOptions.medical_record') },
    {
      id: 'medical_record_request',
      label: t('common.categoryOptions.medical_record_request'),
    },
    { id: 'referral', label: t('common.categoryOptions.referral') },
    {
      id: 'respite_invoice',
      label: t('common.categoryOptions.respite_invoice'),
    },
    { id: 'system_letter', label: t('common.categoryOptions.system_letter') },
    { id: 'result', label: t('common.categoryOptions.result') },
    { id: 'other', label: t('common.categoryOptions.other') },
  ];

  const handleChangeFilterCategory = (event: SelectChangeEvent<string>) => {
    setFilters({ ...filters, category: event.target.value as CategoryType });
  };

  const handleChangeFilterSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: event.target.value });
  };

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleOpenDeleteFileModal = (fileDetails: DocumentFile) => {
    setIsDeleteFileModalOpen(true);
    setSelectedFile(fileDetails);
  };

  const handleCloseDeleteFileModal = () => {
    setIsDeleteFileModalOpen(false);
    setSelectedFile(null);
  };

  const handleClosePreviewFileModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewFile(null);
    reset();
  };

  const handleDownloadPreviewFile = () => {
    setDownloadFile(true);
  };

  const handleDeleteFile = async () => {
    const loadingToast = toast.loading('Deleting  file...', {
      isLoading: true,
      autoClose: 500,
    });

    try {
      await deletePatientDocumentMutation.mutateAsync(selectedFile?.id || '');
      toast.success('Deleted');
      handleCloseDeleteFileModal();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleOpenPreviewFileModal = async (fileDetails: DocumentFile) => {
    setIsPreviewModalOpen(true);
    setSelectedFile(fileDetails);

    const loadingToast = toast.loading('Open  file...', {
      isLoading: true,
      autoClose: 500,
    });

    try {
      await mutateAsync(fileDetails.id);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  return {
    filters,
    setFilters,
    isInitialFilters: JSON.stringify(filters) === JSON.stringify(initialFilter),
    handleChangeFilterCategory,
    handleChangeFilterSearch,
    categoryOptions: categoryOptions.map(o => ({
      value: o.id,
      label: o.label,
    })),
    handleOpenUploadModal,
    handleCloseUploadModal,
    isUploadModalOpen,
    entries,
    handleOpenDeleteFileModal,
    handleCloseDeleteFileModal,
    isDeleteFileModalOpen,
    handleDeleteFile,
    isPreviewModalOpen,
    handleOpenPreviewFileModal,
    handleClosePreviewFileModal,
    selectedFile,
    isLoading,
    count: data.count,
    handleDownloadPreviewFile,
    isFileDownloading,
    previewFile,
  };
};
